// @workflow assistant.ui-tools
/**
 * Show a tag on its level's 2D plan: the one implementation behind the tag card's 2D button
 * (components/search/nodeControls.vue), the 3D icon thumbnail's 2D button
 * (components/common/thumbnailPopup/iconThumbnailPopup.vue), the facility deep link
 * `?tagId=…&show2d=true` (MainView.handleTagShow2DFromUrl) and the assistant's `ui.tags.show_in_2d`
 * (Assistant/domain/tagTools.js).
 *
 * The tag's place (a scaffold's box position, else its point) goes to the store's `zoomToPointIn2D`
 * through the viewer adapter: it opens the 2D view on the plan of the tag's level (`level2D`, else the
 * level whose elevation holds the point) and, once that plan is there, the tag is highlighted on it,
 * which also frames it. `orSelectedLevel` (the deep link from another facility): a `level2D` this
 * facility does not have falls back to the selected level, else the first one.
 *
 * Answers `{ status }`, a TAG_2D_STATUS; STARTED adds `levelName` and `planUrn`, and `onShown(lit)` runs
 * once that plan is there, `lit` false when the plan's labels could not highlight the tag. Each caller keeps its own extras (closing its popup, the deep
 * link's view slot); the choice of level, place and highlight is made here only.
 * `signal` (the assistant's request): once it is aborted nothing more changes on screen, neither the
 * plan (STOPPED) nor, when the plan comes later, the highlight.
 * Relative imports only, so `npm run test:unit` (node --test) can load it directly.
 */
import { PLAN_ZOOM_STATUS } from '../constants/planZoom.js';

export const TAG_2D_STATUS = Object.freeze({
  ...PLAN_ZOOM_STATUS,
  /** The tag is not in the open facility. */
  NOT_FOUND: 'not-found',
  /** The tag has no point to place on a plan. */
  NO_PLACE: 'no-place',
  /** The store gave no answer: nothing says the plan is opening. */
  NOT_SHOWN: 'not-shown',
  /** The caller's request was stopped before the plan was asked for: nothing changed. */
  STOPPED: 'stopped',
});

const isStopped = (signal) => !!(signal && signal.aborted);

/** @param {object|null} tag @returns {object|null} a scaffold's box position, else the tag's point */
export const tagPointOf = (tag) => (tag && (tag.point || tag.position)) || null;

/** The level name to ask for; undefined when the caller wants a fallback and the facility has no level. */
function levelNameFor(tag, state, orSelectedLevel) {
  const levels = state.levels || [];
  const own = tag.level2D || null;
  if (!orSelectedLevel || (own && levels.some((level) => level.name === own))) return own;
  const fallback = state.selectedLevel || levels[0];
  return fallback ? fallback.name : undefined;
}

/**
 * @param {{ store: import('vuex').Store, viewer: object }} ctx `viewer`: viewerFor(store) (platform/viewer)
 * @param {object} tag a facility tag (ForgeBaseTag / ForgeScaffold)
 * @param {{ orSelectedLevel?: boolean, onShown?: (lit: boolean) => void, signal?: AbortSignal|null }} [options]
 * @returns {Promise<{ status: string, levelName?: string, planUrn?: string }>}
 */
export async function showTagIn2d({ store, viewer }, tag, { orSelectedLevel = false, onShown = null, signal = null } = {}) {
  const point = tagPointOf(tag);
  if (!point) return { status: TAG_2D_STATUS.NO_PLACE };
  const levelName = levelNameFor(tag, store.state, orSelectedLevel);
  if (levelName === undefined) return { status: TAG_2D_STATUS.NO_LEVEL };
  if (isStopped(signal)) return { status: TAG_2D_STATUS.STOPPED };
  const highlight = () => {
    if (isStopped(signal)) return;
    const lit = viewer.highlightTagsIn2d([tag]) === true;
    if (onShown) onShown(lit);
  };
  const answer = await viewer.zoomToPointIn2d(point, highlight, { levelName, zoom: false });
  return answer && answer.status ? answer : { status: TAG_2D_STATUS.NOT_SHOWN };
}

/**
 * The same, for a tag id: read in the open facility first (GET_BASE_TAG_BY_ID, as the deep link does).
 * @param {{ store: import('vuex').Store, viewer: object }} ctx
 * @param {string} tagId
 * @param {{ orSelectedLevel?: boolean, onShown?: (lit: boolean) => void, signal?: AbortSignal|null }} [options]
 */
export async function showTagIdIn2d({ store, viewer }, tagId, options) {
  const tag = await store.dispatch('GET_BASE_TAG_BY_ID', { id: tagId, assetId: store.state.assetId, expectedKind: null });
  if (isStopped(options && options.signal)) return { status: TAG_2D_STATUS.STOPPED };
  if (!tag) return { status: TAG_2D_STATUS.NOT_FOUND };
  return showTagIn2d({ store, viewer }, tag, options);
}
